<template>
  <article class="prsc" :class="`prsc--${state}`">
    <!-- Suggestion Head -->
    <div class="prsc__head">
      <div class="prsc__icon" aria-hidden="true">✦</div>
      <span class="prsc__head-text">Suggested range</span>
    </div>

    <!-- Stat Row -->
    <div class="prsc__stats">
      <div class="prsc__stat prsc__stat--min">
        <div class="prsc__stat-label">Suggested Min</div>
        <div class="prsc__stat-value">{{ formatAmount(suggestedMin) }}</div>
      </div>
      <div v-if="median != null" class="prsc__stat prsc__stat--median" data-test="median-tile">
        <div class="prsc__stat-label">Median (reference only)</div>
        <div class="prsc__stat-value">{{ formatAmount(median) }}</div>
      </div>
      <div class="prsc__stat prsc__stat--max">
        <div class="prsc__stat-label">Suggested Max</div>
        <div class="prsc__stat-value">{{ formatAmount(suggestedMax) }}</div>
      </div>
    </div>

    <!-- Explanation -->
    <p class="prsc__explanation">
      Median-of-medians across non-excluded sources. Median shown for reference only — not saved.
    </p>

    <!-- Exclusion Warning (conditional) -->
    <div v-if="excludedSources?.length" class="prsc__warning">
      <span class="prsc__warning-icon" aria-hidden="true">⚠</span>
      <span class="prsc__warning-text">{{ exclusionText }}</span>
    </div>

    <!-- Actions -->
    <div class="prsc__actions">
      <template v-if="state === 'suggested'">
        <button
          type="button"
          class="prsc__btn prsc__btn--dismiss"
          @click="$emit('dismiss')"
        >
          Dismiss
        </button>
        <button
          type="button"
          class="prsc__btn prsc__btn--apply"
          @click="handleApply"
        >
          Apply suggestion
        </button>
      </template>
      <button
        v-else
        type="button"
        class="prsc__btn prsc__btn--undo"
        @click="$emit('undo')"
      >
        Undo
      </button>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue';
import { formatPayRangeWholeAmount } from '@components/utils/formatPayRangeAmount';

const props = defineProps({
  suggestedMin: {
    type: Number,
    required: true,
  },
  suggestedMax: {
    type: Number,
    required: true,
  },
  median: {
    type: Number,
    default: null,
  },
  excludedSources: {
    type: Array,
    default: () => [],
  },
  /** ISO currency code the amounts are shown in (whole units). */
  currency: {
    type: String,
    default: 'USD',
  },
  state: {
    type: String,
    default: 'suggested',
    validator: (val) => ['suggested', 'applied'].includes(val),
  },
});

const emit = defineEmits(['apply', 'dismiss', 'undo']);

const formatAmount = (value) => {
  if (value == null) return '-';
  return formatPayRangeWholeAmount(value, props.currency);
};

const exclusionText = computed(() => {
  if (!props.excludedSources?.length) return '';
  const source = props.excludedSources[0];
  return source.name && source.reason
    ? `${source.name} excluded — ${source.reason}`
    : `${props.excludedSources.length} source(s) excluded`;
});

const handleApply = () => {
  // AC: The median is never part of the emitted apply payload
  emit('apply', {
    suggestedMin: props.suggestedMin,
    suggestedMax: props.suggestedMax,
  });
};
</script>

<style scoped>
/* Card container */
.prsc {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 12px;
  background: #f9f5ff;
  border: 1px solid #e9d7fe;
  max-width: 962px;
}

/* Suggestion Head */
.prsc__head {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 22px;
}

.prsc__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: #7f56d9;
  color: #ffffff;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.prsc__head-text {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

/* Stat Row */
.prsc__stats {
  display: flex;
  gap: 12px;
}

.prsc__stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #eaecf0;
  flex: 1;
  min-width: 0;
}

.prsc__stat--min,
.prsc__stat--max {
  background: #ffffff;
}

.prsc__stat--median {
  background: #f9fafb;
  border-style: dashed;
  border-color: #d0d5dd;
}

.prsc__stat--median .prsc__stat-value {
  color: #667085;
}

.prsc__stat-label {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.prsc__stat-value {
  color: #344054;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

/* Explanation */
.prsc__explanation {
  margin: 0;
  color: #667085;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

/* Warning (conditional) */
.prsc__warning {
  display: flex;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fffaeb;
  border: 1px solid #fedf89;
  align-items: flex-start;
}

.prsc__warning-icon {
  color: #b54708;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  flex-shrink: 0;
}

.prsc__warning-text {
  color: #b54708;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

/* Actions */
.prsc__actions {
  display: flex;
  gap: 8px;
}

.prsc__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 14px;
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  border: 1px solid;
  transition: opacity 0.2s ease;
  min-height: 40px;
}

.prsc__btn:hover {
  opacity: 0.9;
}

.prsc__btn:focus {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.prsc__btn--dismiss {
  background: #ffffff;
  border-color: #d0d5dd;
  color: #344054;
}

.prsc__btn--apply {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #ffffff;
}

.prsc__btn--undo {
  background: #ffffff;
  border-color: #d0d5dd;
  color: #344054;
}
</style>
